import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import { supabase } from "../../lib/supabase";
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Stack,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Modal,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { motion } from "framer-motion";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SubjectIcon from "@mui/icons-material/Subject";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import SEO from "../../components/seo/SEO";

const ContactPage = () => {
  const { t } = useTranslation();
  const form = useRef();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [userName, setUserName] = useState("");

  const benefits = t("contact.benefits", { returnObjects: true }) || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.target);
    const data = {
      name: formData.get("user_name"),
      email: formData.get("user_email"),
      phone: phone,
      subject: formData.get("subject"),
      message: formData.get("message"),
      is_read: false,
      is_replied: false,
      email_sent_status: "pending",
    };

    try {
      // 1. Save to Supabase
      const { data: insertedData, error: supabaseError } = await supabase
        .from("contact_messages")
        .insert([data])
        .select();

      if (supabaseError) throw supabaseError;
      const messageId = insertedData[0].id;

      setModalState({
        open: true,
        type: "success",
        message: t("contact.modal.successMessage", { name: data.name }),
      });

      e.target.reset();
      setPhone("");
      setUserName("");

      // 3. Try to send via EmailJS in background
      try {
        // Create a copy of the form data for EmailJS including the extra field user_phone
        // We use emailjs.send() instead of sendForm() to have full control over the variables
        // without affecting the DOM or Supabase schema
        await emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
          {
            user_name: data.name,
            user_email: data.email,
            user_phone: data.phone,
            subject: data.subject,
            message: data.message,
            to_name: "Alexandre", // Default value for to_name if used in template
          },
          process.env.REACT_APP_EMAILJS_YOUR_PUBLIC_KEY,
        );

        // Update status to success
        await supabase
          .from("contact_messages")
          .update({ email_sent_status: "success" })
          .eq("id", messageId);
      } catch (emailError) {
        console.error("EmailJS background error:", emailError);
        // Update status to failure
        await supabase
          .from("contact_messages")
          .update({ email_sent_status: "failure" })
          .eq("id", messageId);
      }
    } catch (error) {
      console.error("Error saving to Supabase:", error);
      setModalState({
        open: true,
        type: "error",
        message: t("contact.modal.errorMessage"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title={t("contact.seo.title", { defaultValue: "Contact" })}
        description={t("contact.seo.description", {
          defaultValue:
            "Contactez Alexandre SANOU pour vos projets Data Science, Machine Learning et IA. Consultant freelance disponible à Paris.",
        })}
        keywords="contact, consultant, data science, freelance, Paris"
      />
      <Box
        component="main"
        sx={{
          bgcolor: "#0B0F14",
          minHeight: "100vh",
          pb: 20,
          "& .react-tel-input .form-control": {
            width: "100%",
            height: "56px",
            background: "rgba(255,255,255,0.03)",
            border: "none",
            borderRadius: "12px",
            color: "white",
            fontSize: "1rem",
            paddingLeft: "58px",
          },
          "& .react-tel-input .special-label": {
            display: "none",
          },
          "& .react-tel-input .flag-dropdown": {
            background: "transparent",
            border: "none",
            borderRadius: "12px 0 0 12px",
          },
          "& .react-tel-input .selected-flag": {
            background: "transparent",
            paddingLeft: "15px",
            "&:hover, &:focus": {
              background: "rgba(255,255,255,0.05)",
            },
          },
          "& .react-tel-input .country-list": {
            background: "#1F2937",
            color: "white",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "12px",
            marginTop: "10px",
            "& .country:hover": {
              background: "rgba(255,255,255,0.05)",
            },
            "& .country.highlight": {
              background: "rgba(15, 163, 163, 0.1)",
            },
            "& .react-tel-input .search": {
              padding: "8px",
              background: "#1F2937",
            },
            "& .react-tel-input .search-box": {
              width: "100% !important",
              background: "#FFFFFF !important",
              color: "#000000 !important",
              caretColor: "#000000 !important",
              border: "1px solid #D1D5DB !important",
              borderRadius: "8px !important",
              margin: "0 !important",
              padding: "8px 12px !important",
              fontSize: "14px !important",
              outline: "none !important",
            },
            "& .phone-search-input": {
              color: "#000000 !important",
              background: "#FFFFFF !important",
              caretColor: "#000000 !important",
            },
            "& .react-tel-input .search-box:focus": {
              borderColor: "#0FA3A3 !important",
              boxShadow: "0 0 0 2px rgba(15, 163, 163, 0.2) !important",
              caretColor: "#000000 !important",
              color: "#000000 !important",
            },
            "& .react-tel-input .search-box::placeholder": {
              color: "#9CA3AF !important",
            },
          },
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 10, md: 15 },
            bgcolor: "#111827",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 400,
              height: 400,
              bgcolor: "rgba(212, 175, 55, 0.03)",
              borderRadius: "50%",
              filter: "blur(100px)",
              zIndex: 0,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#0FA3A3",
                      fontWeight: 800,
                      letterSpacing: 4,
                      display: "block",
                      mb: 2,
                    }}
                  >
                    {t("contact.header.overline")}
                  </Typography>
                  <Typography
                    variant="h1"
                    sx={{
                      color: "white",
                      mb: 3,
                      fontFamily: "Philosopher",
                      fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                      lineHeight: 1.1,
                    }}
                  >
                    {t("contact.header.titlePrefix")}{" "}
                    <span style={{ color: "#D4AF37" }}>
                      {t("contact.header.titleHighlight")}
                    </span>{" "}
                    {t("contact.header.titleSuffix")}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#9CA3AF",
                      mb: 6,
                      fontWeight: 300,
                      maxWidth: "600px",
                      lineHeight: 1.6,
                    }}
                  >
                    {t("contact.header.subtitle")}
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={4}>
                    {benefits.map((benefit, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <CheckCircleIcon
                          sx={{ color: "#0FA3A3", fontSize: "1.2rem" }}
                        />
                        <Typography
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </motion.div>
              </Grid>

              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <Paper
                    sx={{
                      p: { xs: 4, md: 6 },
                      bgcolor: "#1F2937",
                      borderRadius: "32px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: "white",
                        fontWeight: 800,
                        mb: 4,
                        fontFamily: "Philosopher",
                      }}
                    >
                      {t("contact.form.title")}
                    </Typography>
                    <Box component="form" ref={form} onSubmit={handleSubmit}>
                      <Stack spacing={3}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              name="user_name"
                              label={t("contact.form.fullName")}
                              variant="filled"
                              value={userName}
                              onChange={(e) => setUserName(e.target.value)}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  borderRadius: "12px",
                                  bgcolor: "rgba(255,255,255,0.03)",
                                  color: "white",
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  color: "#9CA3AF",
                                  "&.Mui-focused": { color: "#9CA3AF" },
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              required
                              name="user_email"
                              type="email"
                              label={t("contact.form.email")}
                              variant="filled"
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  borderRadius: "12px",
                                  bgcolor: "rgba(255,255,255,0.03)",
                                  color: "white",
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  color: "#9CA3AF",
                                  "&.Mui-focused": { color: "#9CA3AF" },
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ position: "relative" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  left: 58,
                                  zIndex: 1,
                                  color: "#9CA3AF",
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("contact.form.phone")}
                              </Typography>
                              <PhoneInput
                                country={"fr"}
                                value={phone}
                                onChange={(phone) => setPhone(phone)}
                                enableSearch={true}
                                containerStyle={{ width: "100%" }}
                                searchClass="phone-search-input"
                                inputProps={{
                                  name: "phone_display",
                                  required: true,
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              name="subject"
                              label={t("contact.form.subject")}
                              variant="filled"
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  borderRadius: "12px",
                                  bgcolor: "rgba(255,255,255,0.03)",
                                  color: "white",
                                },
                                startAdornment: (
                                  <SubjectIcon
                                    sx={{
                                      color: "#9CA3AF",
                                      mr: 1,
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                ),
                              }}
                              InputLabelProps={{
                                sx: {
                                  color: "#9CA3AF",
                                  "&.Mui-focused": { color: "#9CA3AF" },
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        <TextField
                          fullWidth
                          required
                          name="message"
                          label={t("contact.form.details")}
                          multiline
                          rows={4}
                          variant="filled"
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              borderRadius: "12px",
                              bgcolor: "rgba(255,255,255,0.03)",
                              color: "white",
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              color: "#9CA3AF",
                              "&.Mui-focused": { color: "#9CA3AF" },
                            },
                          }}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <RocketLaunchIcon />
                            )
                          }
                          sx={{
                            py: 2,
                            borderRadius: "12px",
                            bgcolor: "#D4AF37",
                            color: "#0B0F14",
                            fontWeight: 900,
                            fontSize: "1.1rem",
                            textTransform: "none",
                            "&:hover": {
                              bgcolor: "#B8962E",
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {loading
                            ? t("contact.form.submit.loading")
                            : t("contact.form.submit.idle")}
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Contact Info Cards */}
        <Container
          maxWidth="lg"
          sx={{ mt: { xs: 5, md: -8 }, position: "relative", zIndex: 2 }}
        >
          <Grid container spacing={4} justifyContent="center">
            {[
              {
                icon: <LinkedInIcon />,
                label: t("contact.info.linkedin.label"),
                value: t("contact.info.linkedin.value"),
                color: "#0077B5",
              },
              {
                icon: <LocationOnIcon />,
                label: t("contact.info.location.label"),
                value: t("contact.info.location.value"),
                color: "#D4AF37",
              },
            ].map((info, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Paper
                  sx={{
                    p: 4,
                    bgcolor: "#1F2937",
                    borderRadius: "24px",
                    border: "1px solid rgba(255,255,255,0.05)",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      borderColor: info.color,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: `${info.color}15`,
                      color: info.color,
                      mx: "auto",
                      mb: 2,
                      width: 60,
                      height: 60,
                    }}
                  >
                    {info.icon}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#9CA3AF", fontWeight: 700, mb: 1 }}
                  >
                    {info.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "white", fontWeight: 600 }}
                  >
                    {info.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Closing Trust Section */}
          <Box sx={{ mt: { xs: 10, md: 15 }, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                color: "white",
                mb: 3,
                fontFamily: "Philosopher",
                fontWeight: 700,
              }}
            >
              {t("contact.footer.titlePrefix")}{" "}
              <span style={{ color: "#0FA3A3" }}>
                {t("contact.footer.titleHighlight")}
              </span>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#9CA3AF",
                maxWidth: "800px",
                mx: "auto",
                mb: 6,
                fontSize: "1.2rem",
                lineHeight: 1.6,
                fontWeight: 300,
              }}
            >
              {t("contact.footer.subtitle")}
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center">
              <IconButton
                href="https://www.linkedin.com/in/alexandre-sanou-data-scientist"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: "rgba(255,255,255,0.03)",
                  color: "#9CA3AF",
                  "&:hover": {
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <LinkedInIcon />
              </IconButton>
              <IconButton
                href="https://github.com/SanouAlexandre"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: "rgba(255,255,255,0.03)",
                  color: "#9CA3AF",
                  "&:hover": {
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <GitHubIcon />
              </IconButton>
            </Stack>
          </Box>
        </Container>

        <Modal
          open={modalState.open}
          onClose={() => setModalState((prev) => ({ ...prev, open: false }))}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
            sx: { backdropFilter: "blur(8px)", bgcolor: "rgba(0,0,0,0.8)" },
          }}
        >
          <Fade in={modalState.open}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 450 },
                bgcolor: "#1F2937",
                borderRadius: "32px",
                boxShadow:
                  modalState.type === "success"
                    ? "0 0 40px rgba(76, 175, 80, 0.2)"
                    : "0 0 40px rgba(244, 67, 54, 0.2)",
                p: 6,
                textAlign: "center",
                border: "1px solid",
                borderColor:
                  modalState.type === "success"
                    ? "rgba(76, 175, 80, 0.3)"
                    : "rgba(244, 67, 54, 0.3)",
                outline: "none",
              }}
            >
              <IconButton
                onClick={() =>
                  setModalState((prev) => ({ ...prev, open: false }))
                }
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                <CloseIcon />
              </IconButton>

              {modalState.type === "success" ? (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 80, color: "#4CAF50", mb: 3 }}
                />
              ) : (
                <ErrorOutlineIcon
                  sx={{ fontSize: 80, color: "#F44336", mb: 3 }}
                />
              )}

              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  mb: 2,
                  fontFamily: "Philosopher",
                  fontWeight: 800,
                }}
              >
                {modalState.type === "success"
                  ? t("contact.modal.successTitle")
                  : t("contact.modal.errorTitle")}
              </Typography>

              <Typography
                variant="h6"
                sx={{ color: "#9CA3AF", mb: 4, lineHeight: 1.6 }}
              >
                {modalState.message}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={() =>
                  setModalState((prev) => ({ ...prev, open: false }))
                }
                sx={{
                  bgcolor:
                    modalState.type === "success" ? "#4CAF50" : "#F44336",
                  color: "white",
                  fontWeight: 800,
                  py: 2,
                  borderRadius: "16px",
                  fontSize: "1.1rem",
                  "&:hover": {
                    bgcolor:
                      modalState.type === "success" ? "#388E3C" : "#D32F2F",
                  },
                }}
              >
                {t("contact.modal.close")}
              </Button>
            </Box>
          </Fade>
        </Modal>
      </Box>
    </>
  );
};

export default ContactPage;
